/**
 * The words on Costs & Income — OND-97.
 *
 * The screen sets what a gallery owes ONDO against what its resale agreements
 * earn it. Every sentence here is written to keep two things apart that look
 * alike on a money screen: an amount that is OWED and an amount somebody has
 * RECORDED as moved. ONDO observes no payment and takes no money (OND-51 is
 * still deferred), so nothing on this screen may say "paid" or "received"
 * without "recorded as" in front of it.
 *
 * The arithmetic is `reporting.model.ts`; the reads and the period, currency
 * and sort state are `useReportingController.ts`.
 */

/**
 * The reporting periods, in the order the chips draw them.
 *
 * `custom` is last and is the only one that takes typing. `all-time` is not a
 * period at all — it is the absence of one — and it is in the same list because
 * a reader choosing between "this year" and "everything" is making one choice.
 */
export const REPORTING_PERIODS = [
  "this-month",
  "last-month",
  "this-quarter",
  "last-quarter",
  "this-year",
  "last-year",
  "all-time",
  "custom",
] as const;

export type ReportingPeriodKey = (typeof REPORTING_PERIODS)[number];

export const REPORTING_PERIOD_LABELS: Record<ReportingPeriodKey, string> = {
  "this-month": "This month",
  "last-month": "Last month",
  "this-quarter": "This quarter",
  "last-quarter": "Last quarter",
  "this-year": "This year",
  "last-year": "Last year",
  "all-time": "All time",
  custom: "Custom range",
};

/** What the table can be ordered by. The default is `date`, newest first. */
export const REPORTING_SORTS = ["date", "amount", "work", "side"] as const;
export type ReportingSortKey = (typeof REPORTING_SORTS)[number];

export const REPORTING_SORT_LABELS: Record<ReportingSortKey, string> = {
  date: "Date",
  amount: "Amount",
  work: "Work",
  side: "Cost or income",
};

export const REPORTING_COPY = {
  loading: "Reading your fees and resale settlements…",
  errorTitle: "This report could not be read.",

  lead:
    "What this gallery owes ONDO, and what its post-sale covenants have earned it. " +
    "Both ledgers are read as they stand; nothing here charges, prices, voids or " +
    "settles anything.",

  /**
   * THE REPORTING BASIS, stated on the screen and repeated in the export.
   *
   * One date per side, and the status decides which total a record lands in —
   * so a card and its graph can never mean different things by "March". The
   * alternative, dating each measure by its own event, is rejected in the
   * model's header.
   */
  basis:
    "A fee is dated by the sale that raised it. A resale share is dated by the " +
    "resale. Within a period, each record counts once — as outstanding, or as " +
    "recorded.",
  basisShort: "Fees dated by the sale; shares dated by the resale",

  /** Period boundaries are UTC, and a reader is told rather than left to find out. */
  utcNote: "Dates are counted in UTC, so this report reads the same everywhere.",

  period: {
    legend: "Period",
    /** '1 — 31 March 2026'. */
    range: (from: string, to: string) => `${from} — ${to}`,
    everything: "Every record on both ledgers",
    customFrom: "From",
    customTo: "To",
    customApply: "Apply range",
    customInvalid: "Give a start date on or before the end date.",
    customIncomplete: "Give both a start and an end date.",
  },

  currency: {
    legend: "Currency",
    /** 'USD · 4 records'. */
    choice: (currency: string, records: number) =>
      `${currency} · ${records} ${records === 1 ? "record" : "records"}`,
    /**
     * Said above the totals, because it is the single most surprising thing
     * about this screen: the ONDO tariff is in USD and a covenant is in
     * whatever the first sale was written in, so the two sides are usually not
     * in the same currency at all.
     */
    separate:
      "Each currency is reported on its own. Nothing here is converted, and no " +
      "figure adds two currencies together.",
  },

  totals: {
    /**
     * The currency is named ONCE, over the four tiles, rather than four times
     * inside them.
     *
     * It has to be visible in the totals and this is where it is visible: a
     * heading over the group it governs. Repeating it in each label pushed
     * every label onto two lines, and a tile puts its label above its figure —
     * so the four figures stopped sharing a baseline and the row read as
     * ragged. The label is what had to give, because the figures are the thing
     * being compared.
     */
    heading: (currency: string) => `Totals · ${currency}`,

    feesDue: "ONDO fees outstanding",
    feesPaid: "ONDO fees recorded as paid",
    sharesDue: "Resale share owed to you",
    sharesReceived: "Share recorded as received",

    /** 'from 3 fees'. The note under a tile, saying what made the figure up. */
    fees: (n: number) => `${n} ${n === 1 ? "fee" : "fees"} raised in this period`,
    shares: (n: number) => `${n} ${n === 1 ? "settlement" : "settlements"} in this period`,
    voided: (n: number) => `${n} voided, not counted`,
    /** Counted and NOT added: there is no figure to add until a rate is stated. */
    pending: (n: number) =>
      `${n} awaiting an exchange rate — counted, not added`,
    nothingOwed: (n: number) => `${n} owing nothing`,
  },

  /**
   * The net, and it is said only when it can be.
   *
   * A net needs both sides in ONE currency. Where a gallery's fees are in USD
   * and its covenants in GBP — the ordinary case — there is no net to state and
   * inventing one would mean converting, which this screen does not do.
   */
  net: {
    /** 'Recorded net in GBP: £148,000 received less £0 paid.' */
    recorded: (currency: string, net: string, income: string, cost: string) =>
      `Recorded net in ${currency}: ${net} — ${income} received less ${cost} recorded as paid.`,
    /** Why there is no net line for this currency. */
    oneSided: (currency: string) =>
      `No net is stated for ${currency}: only one of the two ledgers is in it, and ` +
      `this report converts nothing.`,
  },

  charts: {
    comparisonTitle: "Cost against income",
    comparisonHelp:
      "Outstanding amounts are drawn hatched and recorded amounts solid, so a " +
      "figure that is owed never reads as money that has moved.",
    /** The chart's accessible name. The table beneath it is the tabular equivalent. */
    comparisonLabel: (currency: string) =>
      `Bar chart comparing ONDO fees with resale shares in ${currency}. ` +
      `The table below carries the same figures.`,
    timeTitle: "Over the period",
    timeLabel: (currency: string, grouping: string) =>
      `Column chart of ONDO fees and resale shares in ${currency}, grouped ${grouping}. ` +
      `The table below carries the same figures.`,
    byDay: "by day",
    byMonth: "by month",
    legend: {
      feesDue: "ONDO fees outstanding",
      feesPaid: "ONDO fees recorded as paid",
      sharesDue: "Resale share owed to you",
      sharesReceived: "Resale share recorded as received",
    },
    axisLabel: (currency: string) => `Amount (${currency})`,
    noData: "Nothing was recorded in this period, so there is nothing to plot.",
    /** A bucket with nothing in it is drawn, not skipped — the axis stays continuous. */
    emptyBucket: "nothing recorded",
  },

  table: {
    title: "The records behind these figures",
    sortLegend: "Order by",
    columns: {
      side: "Cost or income",
      record: "Record",
      work: "Work",
      date: "Date",
      status: "Status",
      amount: "Amount",
      currency: "Currency",
    },
    /** The row's link. Both a fee and a settlement are about one work. */
    openWork: "Open the work record",
    empty: "No fee and no resale settlement falls in this period.",
    emptyAllTime:
      "This gallery has recorded no sale that raised a fee, and no covenant of " +
      "its own has produced a settlement yet.",
    showVoid: (n: number) => `Show ${n} voided ${n === 1 ? "record" : "records"}`,
    hideVoid: "Hide voided records",
    voidNote:
      "A voided record was owed and then was not — the sale behind it was " +
      "withdrawn. It is listed so it can be inspected and is counted in no total.",
  },

  sides: {
    fee: "ONDO fee",
    share: "Resale share",
  },

  /** 'Registration fee · premium'. The tier is the whole of what a fee says. */
  feeKind: {
    plain: "Registration fee · base",
    premium: "Registration fee · premium, covenant attached",
  },

  /** 'Post-sale covenant · 40% of profit, your 50%'. */
  shareKind: (share: string, gallerySplit: string) =>
    `Post-sale covenant · ${share} of profit, your ${gallerySplit}`,

  status: {
    due: "Outstanding",
    recorded: "Recorded",
    pending: "Awaiting a rate",
    nothing_owed: "Nothing owed",
    void: "Voided",
  },

  export: {
    csv: "Export CSV",
    print: "Print this report",
    working: "Preparing…",
    /** Said beside the buttons, because an export is a claim about its scope. */
    note:
      "The CSV carries every record in the chosen period and currency, not just " +
      "the rows on screen, and repeats the four totals so a spreadsheet can be " +
      "reconciled against them.",
    filename: (currency: string, from: string, to: string) =>
      `ondo-costs-and-income-${currency}-${from}-to-${to}.csv`,
    filenameAllTime: (currency: string) => `ondo-costs-and-income-${currency}-all-time.csv`,
    failed: "The file could not be prepared.",
  },

  /**
   * In the file, so the claim travels with the figures.
   *
   * The same sentence the screen makes, for the same reason
   * `collector-list-export.ts` puts its stamp note inside the CSV: a reader
   * opening a spreadsheet months later has only the file.
   */
  csvNote:
    "Amounts recorded as paid or received are this gallery's own record. ONDO " +
    "observes no payment and takes no money. Voided records are listed and are " +
    "counted in no total. Nothing in this file is converted between currencies.",

  csvHeadings: {
    document: "ONDO — costs and income",
    period: "Period",
    periodFrom: "From (UTC)",
    periodTo: "To (UTC)",
    currency: "Currency",
    basis: "Reporting basis",
    statuses: "Statuses included",
    exportedAt: "Exported at",
    records: "Records",
    note: "Note",
    feesDue: "Total — ONDO fees outstanding",
    feesPaid: "Total — ONDO fees recorded as paid",
    sharesDue: "Total — resale share owed to you",
    sharesReceived: "Total — resale share recorded as received",
  },

  errors: {
    refused: "This account is no longer a member of this gallery.",
  },
} as const;
